import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
// api
import { getSubDistributor } from "../../../api/distributor-api";
// utils
import logger from "../../../utils/logger";
//
import navConfig from "./config-navigation";

// Constants for SPAR sub-distributor configuration
export const SPAR_GROUP_CUSTOMER_NAME = "The SPAR Group Limited";
export const SUB_DISTRIBUTOR_ROLES = [
  "sub-distributor",
  "sub_distributor_manager",
  "sub_distributor_finance",
];
/** Level 1: hide Retail Commission only (Promo Engine visible). */
export const RESTRICTED_NAV_TITLES_LEVEL_1 = ["Retail Commission"];
/** Level 2: hide Promo Engine and Retail Commission. */
export const RESTRICTED_NAV_TITLES_LEVEL_2 = ["Promo Engine", "Retail Commission"];

/**
 * Custom hook to fetch sub-distributor level and filter navigation config
 * Level 1: Retail Commission hidden, Promo Engine visible. Level 2: both hidden. Level 3: full nav.
 * @returns {Object} Filtered navigation config
 */
export function useSubDistributorNavConfig() {
  const selectedRole = useSelector((state) => state.auth.selectedRole);
  const [subDistributorLevel, setSubDistributorLevel] = useState(null);

  // Fetch sub-distributor level for filtering navigation
  useEffect(() => {
    let cancelled = false;

    const fetchSubDistributorLevel = async () => {
      if (!selectedRole) return;

      const isSubDistributorRole = SUB_DISTRIBUTOR_ROLES.includes(selectedRole.role);

      if (
        isSubDistributorRole &&
        selectedRole.sub_distributor_id &&
        selectedRole.distributor_i_customer &&
        selectedRole.customer_name === SPAR_GROUP_CUSTOMER_NAME
      ) {
        try {
          const response = await getSubDistributor(
            selectedRole.distributor_i_customer,
            selectedRole.sub_distributor_id
          );
          if (!cancelled && response?.status_code === 1001 && response?.data) {
            setSubDistributorLevel(Number(response.data.level));
          }
        } catch (error) {
          if (!cancelled) {
            logger.error("Error fetching sub-distributor level", error, {
              subDistributorId: selectedRole.sub_distributor_id,
            });
          }
        }
      }
    };

    fetchSubDistributorLevel();

    return () => {
      cancelled = true;
    };
  }, [selectedRole]);

  // Level 1 = hide Retail Commission only; Level 2 = hide Promo Engine + Retail Commission; Level 3 = full nav
  const filteredNavConfig = useMemo(() => {
    const isSubDistributorRole =
      selectedRole?.role && SUB_DISTRIBUTOR_ROLES.includes(selectedRole.role);

    if (!isSubDistributorRole || selectedRole?.customer_name !== SPAR_GROUP_CUSTOMER_NAME) {
      return navConfig;
    }

    const restrictedTitles =
      subDistributorLevel === 1
        ? RESTRICTED_NAV_TITLES_LEVEL_1
        : subDistributorLevel === 2
          ? RESTRICTED_NAV_TITLES_LEVEL_2
          : null;

    if (!restrictedTitles) {
      return navConfig;
    }

    return navConfig.map((group) => {
      if (group.items) {
        return {
          ...group,
          items: group.items.map((item) => {
            if (item.children) {
              return {
                ...item,
                children: item.children.filter(
                  (child) => !restrictedTitles.includes(child.title)
                ),
              };
            }
            return item;
          }),
        };
      }
      return group;
    });
  }, [subDistributorLevel, selectedRole]);

  return filteredNavConfig;
}
